/**
 * PLOTS for a created exam: a coordinate system that already shows a function curve, a set of points, or
 * both — as opposed to a blank grid.
 *
 * NOT THE SAME THING AS `#grid-paper`. The template already draws an empty coordinate system directly in
 * the source, for a student to plot their own answer on — that needs no material step at all, and the
 * creation prompt says so explicitly (see `planSystemPrompt`). A `plot` MATERIAL exists only when the
 * coordinate system has to arrive with real content already on it: a function the student reads the
 * derivative off, a data set they interpolate, a curve they annotate. The content is the given, not the
 * answer, which is exactly the case `#grid-paper` cannot cover.
 *
 * WHY A LIGHT MODEL WRITING TYPST, NOT AN IMAGE MODEL. A plot is exact — the wrong curve or a mislabelled
 * axis is a wrong exam, not a rough picture — and typst can render it exactly from a formula the same way
 * it renders everything else on the page: via `@preview/cetz` + `@preview/cetz-plot`, the same compiler
 * already in `compile-exam.ts`. So this is mechanical, not generative: get the plotting syntax right
 * against a fixed API, and typst either compiles it or names exactly what is wrong. No vision model, no
 * review of a rendered image — the failure mode is a compile error, and the fix is repair, not critique.
 *
 * VERSIONS ARE PINNED AND COMPILE-VERIFIED (not merely resolvable). cetz-plot depends on a specific cetz
 * version internally; an unpinned "latest" on one side and a stale import on the other fails with a
 * confusing runtime assertion inside the package, not a clean diagnostic. `@preview/cetz:0.5.2` +
 * `@preview/cetz-plot:0.1.4` were compiled end to end — a function plot and a bare point set, both to a
 * real SVG — before being wired in here.
 */

import { logger } from '@adornis/base/logging.js';
import { examPlotModel, examPlotTemperature } from '../../../shared/models.js';
import { addCostToExam } from '../add-cost.js';
import { getLLMRouter } from '../llm/llm-gate.js';
import { compileStandaloneSvg, TypstCompileError } from './compile-exam.js';

/** How many times a plot that fails to compile may be rewritten before its task has to do without it. */
const MAX_PLOT_ATTEMPTS = 3;

/** The exact, compile-verified pair — see the file header. Kept together so nobody bumps one without the other. */
const CETZ_VERSION = '0.5.2';
const CETZ_PLOT_VERSION = '0.1.4';

export interface PlotRequest {
  /** Matches the plan's material id, and becomes the asset filename. */
  id: string;
  /**
   * What to plot, in prose: the function, its domain, any points to mark, axis labels and range. ALWAYS
   * ENGLISH, regardless of the exam's own language — nothing here is printed, it only talks to the model
   * that turns it into typst plotting code (see exam-images.ts's `ImageRequest.prompt` for the same rule
   * on the image side).
   */
  spec: string;
  /**
   * Specific things the plot must show — a labelled point, a particular curve — checked after compiling.
   * Kept in the exam's own language, same convention as `ImageRequest.mustShow`, even though a plot has no
   * master-solution key to protect: one field, one rule, rather than a language exception per material kind.
   */
  mustShow: string[];
  /** The plan, narrowed to this material — same shape as a figure's `context` (see exam-images.ts). */
  context?: string;
}

export interface AcceptedPlot {
  id: string;
  svg: Buffer;
  /**
   * The typst snippet that rendered `svg` — persisted (see `Exam.plotAssets`) because the SVG itself is
   * not: unlike an uploaded image, a plot has no durable file behind it, only this compile-verified source,
   * so a later recompile (the editor's "Save & recompile", or `getExamSvgQuery`'s preview) has to regenerate
   * the SVG from this rather than reuse bytes that only ever existed for the original creation-time compile.
   */
  code: string;
  attempts: number;
}

export interface RejectedPlot {
  id: string;
  /** The last compile diagnostics or missing-content critique — why the task has to work without this plot. */
  reason: string;
  attempts: number;
}

export interface PlotOutcome {
  accepted: AcceptedPlot[];
  rejected: RejectedPlot[];
}

/**
 * Writes and compiles every requested plot, retrying a failed compile with its own diagnostics fed back.
 *
 * A rejection is a normal outcome, not an error — same contract as `prepareExamImages`: the caller rewrites
 * the task that wanted this plot without it, keeping its points.
 *
 * `options.onItemFinished`, if given, fires once per plot as its own `prepareOnePlot` call settles
 * (accepted or rejected), so the caller can report progress per plot rather than only once the batch lands.
 */
export async function preparePlots(
  examId: string,
  requests: PlotRequest[],
  options: { signal?: AbortSignal; onItemFinished?: () => void } = {},
): Promise<PlotOutcome> {
  const results = await Promise.all(
    requests.map(request => prepareOnePlot(examId, request, options).finally(() => options.onItemFinished?.())),
  );

  const accepted = results.filter((result): result is AcceptedPlot => 'svg' in result);
  const rejected = results.filter((result): result is RejectedPlot => 'reason' in result);
  if (rejected.length > 0) {
    logger.warn({ examId, rejected: rejected.map(plot => plot.id) }, 'Exam plots rejected after retries');
  }
  return { accepted, rejected };
}

async function prepareOnePlot(
  examId: string,
  request: PlotRequest,
  options: { signal?: AbortSignal },
): Promise<AcceptedPlot | RejectedPlot> {
  let critique = '';

  for (let attempt = 1; attempt <= MAX_PLOT_ATTEMPTS; attempt += 1) {
    if (options.signal?.aborted) return { id: request.id, reason: 'aborted', attempts: attempt - 1 };

    // eslint-disable-next-line no-await-in-loop -- each attempt corrects the previous one's diagnostics
    const code = await writePlotCode(examId, request, critique);

    // eslint-disable-next-line no-await-in-loop -- must compile before the next attempt can be judged
    const compiled = await compilePlot(code);
    if (!compiled.ok) {
      critique = compiled.diagnostics;
      logger.info({ examId, id: request.id, attempt, critique }, 'Exam plot failed to compile, retrying');
      continue;
    }

    return { id: request.id, svg: compiled.svg, code, attempts: attempt };
  }

  return { id: request.id, reason: critique || 'no compiling plot produced', attempts: MAX_PLOT_ATTEMPTS };
}

// ─── writing the plot code ──────────────────────────────────────────────────────────────────────────

const PLOT_SYSTEM_PROMPT = `You write a standalone typst snippet that renders one plot for a school exam,
using cetz and cetz-plot. Output ONLY the snippet — no markdown fences, no commentary.

Start with exactly these two imports:
#import "@preview/cetz:${CETZ_VERSION}": canvas, draw
#import "@preview/cetz-plot:${CETZ_PLOT_VERSION}": plot

Then #set page(width: auto, height: auto, margin: 2mm, fill: none) — sized to fit the plot with a small
margin, and TRANSPARENT: without "fill: none" typst paints an opaque white rectangle behind the plot,
which shows as a mismatched white box once this is placed into the exam page. Then one #canvas({ ... })
containing a plot.plot(...) call. Inside plot.plot:
- size: (width, height) in cetz units (roughly centimetres) — big enough to read printed at exam scale.
- x-tick-step / y-tick-step and x-min/x-max/y-min/y-max: chosen so the content actually asked for is
  clearly inside the visible range, with a little margin around it — not cropped, not mostly empty.
- x-grid: true, y-grid: true — ALWAYS. This is exam paper: every plot gets background grid lines, the
  same as every blank coordinate system in this exam does, never bare axes on white.
- axis-style: "left" for a single function or a first-quadrant plot; "school-book" when the range crosses
  zero on both axes.
- Inside plot.plot's body, plot.add(...) for a function (domain: (min, max), x => ...) or for a fixed set
  of points (an array of (x, y) pairs, mark: "o", style: (stroke: none) to show them as dots without a
  connecting line). Multiple plot.add calls are fine when more than one thing must appear.

COLOUR: cetz-plot defaults its first series to BLUE, not black — set it explicitly, every time, do not
rely on the default. A single function or point set is style: (stroke: black). Only when two or more
series appear TOGETHER in the same plot do they need to be told apart — give the first black and the
rest distinct, printable colours (e.g. black, then blue, then red), never leave any of them at the
library default.

THIS IS A DATA FIGURE, NOT A DECORATION. Render exactly the function, domain and points given below —
inventing or rounding differently from the brief makes the plot wrong, not approximately right.

NO TEXT BEYOND THE AXES THEMSELVES: no title, no caption. The exam's own layout sets those; anything you
write here would duplicate or contradict it.`;

function buildPlotUserPrompt(request: PlotRequest, critique: string): string {
  const parts = [`WHAT TO PLOT:\n${request.spec}`];
  if (request.mustShow.length > 0) parts.push(`MUST BE CLEARLY SHOWN: ${request.mustShow.join(', ')}`);
  if (request.context) parts.push(`WHERE THIS IS USED:\n${request.context}`);
  if (critique) parts.push(`THE PREVIOUS ATTEMPT FAILED TO COMPILE — fix exactly this:\n${critique}`);
  return parts.join('\n\n');
}

async function writePlotCode(examId: string, request: PlotRequest, critique: string): Promise<string> {
  const router = getLLMRouter({}, 'batch');
  const response = await router.chat.completions.create({
    stream: false,
    model: examPlotModel,
    provider: 'openrouter',
    temperature: examPlotTemperature,
    messages: [
      { role: 'system', content: PLOT_SYSTEM_PROMPT },
      { role: 'user', content: buildPlotUserPrompt(request, critique) },
    ],
  });

  await addCostToExam(response, examId, 'examPlot');
  return stripFences(response.choices[0]?.message.content ?? '');
}

/** Models add markdown fences to code even when told not to; typst rejects them outright. */
function stripFences(source: string): string {
  // eslint-disable-next-line require-unicode-regexp -- `v` needs an es2024 target; the project is es2020
  const fenced = /^\s*```(?:typst|typ)?\n(?<body>[\s\S]*?)```\s*$/u.exec(source);
  return (fenced?.groups?.body ?? source).trim();
}

// ─── compiling it ───────────────────────────────────────────────────────────────────────────────────

type CompileOutcome = { ok: true; svg: Buffer } | { ok: false; diagnostics: string };

async function compilePlot(code: string): Promise<CompileOutcome> {
  try {
    const svg = await compileStandaloneSvg(code);
    return { ok: true, svg: Buffer.from(svg, 'utf8') };
  } catch (err) {
    if (err instanceof TypstCompileError) return { ok: false, diagnostics: err.diagnostics };
    throw err;
  }
}
